import { JsonProperty, JsonObject, JsonConverter, JsonConvert } from 'json2typescript';

/**
 * This class allows to create a custom Error with additional fields
 * @dynamic
 */
class ApolloError extends Error {
    constructor(source) {
        super();
        this.name = "ApolloError";
        Object.assign(this, source);
        // Known bug of typescript when trying to make a custom Error by extending the Error class:
        // the prototype will not be set correctly unless doing it manually
        Object.setPrototypeOf(this, ApolloError.prototype);
    }
}
/**
 * Return the error in a string ready to be logged
 * @param error
 */
const formatApolloError = (error) => `${error.source} : ${error.route} -> ${error.status} ${error.statusText} : ${error.message} ${error.stack}`;

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * This interface is used to describe the health of a service
 * @dynamic
 */
let HealthCheck = class HealthCheck {
    constructor(source) {
        this.componentId = undefined;
        this.componentType = undefined;
        this.status = undefined;
        this.time = undefined;
        Object.assign(this, source);
    }
};
__decorate([
    JsonProperty("component_id", String, true),
    __metadata("design:type", String)
], HealthCheck.prototype, "componentId", undefined);
__decorate([
    JsonProperty("component_type", String, true),
    __metadata("design:type", String)
], HealthCheck.prototype, "componentType", undefined);
__decorate([
    JsonProperty("status", String, true),
    __metadata("design:type", String)
], HealthCheck.prototype, "status", undefined);
__decorate([
    JsonProperty("time", String, true),
    __metadata("design:type", String)
], HealthCheck.prototype, "time", undefined);
HealthCheck = __decorate([
    JsonObject("HealthCheck"),
    __metadata("design:paramtypes", [Object])
], HealthCheck);

/**
 * Custom converter for a Record of HealthCheck objects
 */
let HealthChecksConverter = class HealthChecksConverter {
    /**
     * transform a typed record of HealthCheck to a serializable object
     * @param checks
     */
    serialize(checks) {
        const result = {};
        const jsonConvert = new JsonConvert();
        for (const key of Object.keys(checks)) {
            result[key] = jsonConvert.serialize(checks[key], HealthCheck);
        }
        return result;
    }
    /**
     * transform a serialized js to a typed record of HealthCheck
     * @param checks
     */
    deserialize(checks) {
        const result = {};
        const jsonConvert = new JsonConvert();
        for (const key of Object.keys(checks)) {
            result[key] = jsonConvert.deserialize(checks[key], HealthCheck);
        }
        return result;
    }
};
HealthChecksConverter = __decorate([
    JsonConverter
], HealthChecksConverter);

/**
 * Abstract Class used by every Model class in the App
 */
class ModelObject {
    /**
     * This static function is used to populate an object from his equivalent in JSON
     * @param jsonData
     * @param typeToGen
     */
    static fromJsonData(jsonData, typeToGen) {
        // An error is thrown with undefined values
        if (jsonData === undefined || jsonData === null) {
            return jsonData;
        }
        const json = jsonData instanceof String
            ? JSON.parse(jsonData)
            : jsonData;
        return new JsonConvert().deserializeObject(json, typeToGen);
    }
    /**
     * Generate Json from inherit object
     */
    toJsonData() {
        return new JsonConvert().serializeObject(this);
    }
}

/**
 * This interface is used to describe for a health WS Response
 */
let HealthResponse = class HealthResponse extends ModelObject {
    constructor() {
        super();
        /** Timestamp of the request */
        this.timestamp = undefined;
        /** Status of the service */
        this.status = undefined;
        /** Version of the service */
        this.version = undefined;
        /** HealthChecks notes for the related services  */
        this.notes = undefined;
        /** ID of the service */
        this.serviceId = undefined;
        /** description of the service */
        this.description = undefined;
        /** HealthCheck status for the related service  */
        this.checks = undefined;
        this.timestamp = Date.now();
    }
};
__decorate([
    JsonProperty("timestamp", Number),
    __metadata("design:type", Number)
], HealthResponse.prototype, "timestamp", undefined);
__decorate([
    JsonProperty("status", String, true),
    __metadata("design:type", String)
], HealthResponse.prototype, "status", undefined);
__decorate([
    JsonProperty("version", String, true),
    __metadata("design:type", String)
], HealthResponse.prototype, "version", undefined);
__decorate([
    JsonProperty("notes", [String], true),
    __metadata("design:type", Array)
], HealthResponse.prototype, "notes", undefined);
__decorate([
    JsonProperty("service_id", String, true),
    __metadata("design:type", String)
], HealthResponse.prototype, "serviceId", undefined);
__decorate([
    JsonProperty("description", String),
    __metadata("design:type", String)
], HealthResponse.prototype, "description", undefined);
__decorate([
    JsonProperty("checks", HealthChecksConverter, true),
    __metadata("design:type", Object)
], HealthResponse.prototype, "checks", undefined);
HealthResponse = __decorate([
    JsonObject("HealthResponse"),
    __metadata("design:paramtypes", [])
], HealthResponse);

/**
 * Request wrapper, allow to send common attributes in the BODY
 * @dynamic
 */
let Request = class Request {
    constructor(source) {
        Object.assign(this, source);
    }
};
Request = __decorate([
    JsonObject("Request"),
    __metadata("design:paramtypes", [Object])
], Request);

/**
 * Wrapper for response in body, encapsulate common fields.
 * @dynamic
 */
let Response = class Response {
    constructor(source) {
        Object.assign(this, source);
    }
};
__decorate([
    JsonProperty("timestamp"),
    __metadata("design:type", String)
], Response.prototype, "timestamp", undefined);
__decorate([
    JsonProperty("error", ApolloError, true),
    __metadata("design:type", ApolloError)
], Response.prototype, "error", undefined);
Response = __decorate([
    JsonObject("Response"),
    __metadata("design:paramtypes", [Object])
], Response);

export { ApolloError, HealthCheck, HealthResponse, ModelObject, Request, Response, formatApolloError };
